import { createAppointmentStepOneDraftFingerprint } from "@/app/booking/core/appointmentDraftFingerprint";
import type { AppointmentBookingDraftV1 } from "@/app/booking/core/appointmentBookingDraft";
import { isResultsAppointmentDraftId } from "@/app/booking/core/draftId";
import {
  resolveAppointmentResultsEntryPreference,
  type AppointmentResultsEntryPreferenceV1,
} from "@/app/booking/results/appointmentResultsEntryPreference";
import {
  validateSpecialistLocationGatewayNavigationAuthority,
  type SpecialistLocationGatewayCommittedNavigationLineageV1,
} from "@/app/specialists/locationGateway/contract";
import { createSpecialistLocationGatewayAppointmentDestinationFingerprintFromStepOneFingerprint } from "@/app/specialists/locationGateway/appointmentDestinationFingerprint";

import {
  createAppointmentWaitlistReceipt,
  createRetiredGroupAppointmentWaitlistReceiptForStorageMigration,
  validateAppointmentWaitlistReceipt,
  validateRetiredGroupAppointmentWaitlistReceiptForStorageMigration,
  type AppointmentWaitlistReceiptV3,
} from "./appointmentWaitlistReceipt";
import {
  validateAppointmentWaitlistSubmissionAttempt,
  validateRetiredGroupAppointmentWaitlistSubmissionAttemptForStorageMigration,
  type AppointmentWaitlistSubmissionAttemptV1,
} from "./appointmentWaitlistSubmissionAttempt";

export const APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_SCHEMA_VERSION =
  2 as const;
export const APPOINTMENT_WAITLIST_TERMINAL_RESULTS_ENTRY_PROOF_SCHEMA_VERSION =
  1 as const;

export type AppointmentWaitlistTerminalAttemptOriginRequirement =
  | "legacy-non-gateway"
  | "gateway";

export interface AppointmentWaitlistTerminalResultsEntryProofV1 {
  readonly schemaVersion: typeof APPOINTMENT_WAITLIST_TERMINAL_RESULTS_ENTRY_PROOF_SCHEMA_VERSION;
  readonly kind: "appointment-waitlist-terminal-results-entry-proof";
  readonly handoffId: string;
  readonly customerAccountId: string;
  readonly locale: string;
  readonly sourceAppointmentDraft: AppointmentBookingDraftV1;
  readonly preference: AppointmentResultsEntryPreferenceV1;
  /** The last live/persisted proof epoch before the durable Attempt commit. */
  readonly capturedAtEpochMs: number;
}

/**
 * Canonical, single-key Waitlist terminal-attempt authority. The older
 * Attempt, Receipt, and Specialist terminal-origin stores are compatibility
 * projections only and are deliberately absent from this contract.
 */
export interface AppointmentWaitlistTerminalAttemptAggregateV2 {
  readonly schemaVersion: typeof APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_SCHEMA_VERSION;
  readonly kind: "appointment-waitlist-terminal-attempt-aggregate";
  readonly draftId: string;
  readonly customerAccountId: string;
  readonly originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  readonly revision: number;
  readonly attempt: AppointmentWaitlistSubmissionAttemptV1;
  readonly resultsEntryProof: AppointmentWaitlistTerminalResultsEntryProofV1 | null;
  readonly gatewayLineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
  readonly receipt: AppointmentWaitlistReceiptV3 | null;
  readonly acknowledgedAtEpochMs: number | null;
}

/** @deprecated Compatibility alias for callers compiled against the V1 type name. */
export type AppointmentWaitlistTerminalAttemptAggregateV1 =
  AppointmentWaitlistTerminalAttemptAggregateV2;

export type AppointmentWaitlistTerminalAttemptAggregateValidation =
  | {
      valid: true;
      aggregate: AppointmentWaitlistTerminalAttemptAggregateV1;
    }
  | {
      valid: false;
      reason: "invalid-appointment-waitlist-terminal-attempt-aggregate";
    };

export type AppointmentWaitlistTerminalAttemptAggregateV1ForStorageMigration =
  Omit<
    AppointmentWaitlistTerminalAttemptAggregateV2,
    "schemaVersion" | "resultsEntryProof"
  > & {
    readonly schemaVersion: 1;
  };

export type AppointmentWaitlistTerminalAttemptAggregateV1StorageMigrationValidation =
  | {
      valid: true;
      aggregate: AppointmentWaitlistTerminalAttemptAggregateV1ForStorageMigration;
    }
  | {
      valid: false;
      reason: "invalid-appointment-waitlist-terminal-attempt-aggregate";
    };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
) {
  const actual = Object.keys(value).sort();
  const canonical = [...expected].sort();
  return (
    actual.length === canonical.length &&
    actual.every((key, index) => key === canonical[index])
  );
}

export function isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(
  value: unknown,
): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= 8_640_000_000_000_000
  );
}

function isIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= 512 &&
    !/[\u0000-\u001f\u007f]/u.test(value)
  );
}

function sameSnapshot(left: unknown, right: unknown) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

function cloneAppointmentWaitlistTerminalResultsEntryProofForAttempt(input: {
  value: unknown;
  sourceAppointmentDraft: AppointmentBookingDraftV1;
  customerAccountId: string;
  activatedAtEpochMs?: number;
}) {
  if (
    !isResultsAppointmentDraftId(
      input.sourceAppointmentDraft.draftId,
    )
  ) {
    return input.value === null ? null : undefined;
  }
  if (
    !isRecord(input.value) ||
    !hasExactKeys(input.value, [
      "schemaVersion",
      "kind",
      "handoffId",
      "customerAccountId",
      "locale",
      "sourceAppointmentDraft",
      "preference",
      "capturedAtEpochMs",
    ]) ||
    input.value.schemaVersion !==
      APPOINTMENT_WAITLIST_TERMINAL_RESULTS_ENTRY_PROOF_SCHEMA_VERSION ||
    input.value.kind !==
      "appointment-waitlist-terminal-results-entry-proof" ||
    !isIdentifier(input.value.handoffId) ||
    !isIdentifier(input.value.customerAccountId) ||
    !isIdentifier(input.value.locale) ||
    !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(
      input.value.capturedAtEpochMs,
    ) ||
    (input.activatedAtEpochMs !== undefined &&
      input.value.capturedAtEpochMs < input.activatedAtEpochMs) ||
    input.value.customerAccountId !== input.customerAccountId ||
    !sameSnapshot(
      input.value.sourceAppointmentDraft,
      input.sourceAppointmentDraft,
    )
  ) {
    return undefined;
  }
  const resolved = resolveAppointmentResultsEntryPreference({
    preference: input.value.preference,
    sourceAppointmentDraft: input.value.sourceAppointmentDraft,
    expectedCustomerAccountId: input.customerAccountId,
    expectedLocale: input.value.locale,
    expectedHandoffId: input.value.handoffId,
    nowEpochMs: input.value.capturedAtEpochMs,
  });
  if (
    resolved.status !== "ready" ||
    resolved.preference.handoffId !== input.value.handoffId ||
    resolved.preference.customerAccountId !== input.value.customerAccountId ||
    resolved.preference.locale !== input.value.locale
  ) {
    return undefined;
  }
  return Object.freeze({
    schemaVersion:
      APPOINTMENT_WAITLIST_TERMINAL_RESULTS_ENTRY_PROOF_SCHEMA_VERSION,
    kind: "appointment-waitlist-terminal-results-entry-proof" as const,
    handoffId: resolved.preference.handoffId,
    customerAccountId: input.customerAccountId,
    locale: resolved.preference.locale,
    sourceAppointmentDraft: input.sourceAppointmentDraft,
    preference: resolved.preference,
    capturedAtEpochMs: input.value.capturedAtEpochMs as number,
  }) satisfies AppointmentWaitlistTerminalResultsEntryProofV1;
}

export function appointmentWaitlistTerminalResultsEntryProofMatchesAttempt(
  input: {
    proof: unknown;
    attempt: AppointmentWaitlistSubmissionAttemptV1;
  },
) {
  const proof = cloneAppointmentWaitlistTerminalResultsEntryProofForAttempt({
    value: input.proof,
    sourceAppointmentDraft: input.attempt.sourceAppointmentDraft,
    customerAccountId: input.attempt.customerAccountId,
    activatedAtEpochMs: input.attempt.activatedAtEpochMs,
  });
  return (
    proof !== undefined &&
    (proof === null
      ? input.proof === null
      : sameSnapshot(proof, input.proof))
  );
}

export function createAppointmentWaitlistTerminalResultsEntryProof(input: {
  sourceAppointmentDraft: unknown;
  preference: unknown;
  expectedCustomerAccountId: string;
  expectedLocale: string;
  expectedHandoffId: string;
  capturedAtEpochMs: number;
}) {
  const resolved = resolveAppointmentResultsEntryPreference({
    preference: input.preference,
    sourceAppointmentDraft: input.sourceAppointmentDraft,
    expectedCustomerAccountId: input.expectedCustomerAccountId,
    expectedLocale: input.expectedLocale,
    expectedHandoffId: input.expectedHandoffId,
    nowEpochMs: input.capturedAtEpochMs,
  });
  if (
    resolved.status !== "ready" ||
    !isResultsAppointmentDraftId(resolved.preference.draftId)
  ) {
    return null;
  }
  const candidate = {
    schemaVersion:
      APPOINTMENT_WAITLIST_TERMINAL_RESULTS_ENTRY_PROOF_SCHEMA_VERSION,
    kind: "appointment-waitlist-terminal-results-entry-proof" as const,
    handoffId: resolved.preference.handoffId,
    customerAccountId: input.expectedCustomerAccountId,
    locale: input.expectedLocale,
    sourceAppointmentDraft: input.sourceAppointmentDraft,
    preference: resolved.preference,
    capturedAtEpochMs: input.capturedAtEpochMs,
  };
  return (
    cloneAppointmentWaitlistTerminalResultsEntryProofForAttempt({
      value: candidate,
      sourceAppointmentDraft:
        input.sourceAppointmentDraft as AppointmentBookingDraftV1,
      customerAccountId: input.expectedCustomerAccountId,
    }) ?? null
  );
}

function attemptValidationEpoch(value: unknown) {
  if (
    !isRecord(value) ||
    !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(
      value.activatedAtEpochMs,
    ) ||
    !isRecord(value.state)
  ) {
    return null;
  }
  if (value.state.kind === "pending-reconciliation") {
    return value.activatedAtEpochMs;
  }
  if (
    value.state.kind === "accepted" &&
    isRecord(value.state.submission) &&
    isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(
      value.state.submission.acceptedAtEpochMs,
    )
  ) {
    return Math.max(
      value.activatedAtEpochMs,
      value.state.submission.acceptedAtEpochMs,
    );
  }
  if (
    value.state.kind === "not-applied" &&
    isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(
      value.state.confirmedAtEpochMs,
    )
  ) {
    return Math.max(value.activatedAtEpochMs, value.state.confirmedAtEpochMs);
  }
  return null;
}

function cloneAppointmentWaitlistTerminalAttemptForMode(
  value: unknown,
  expectedMode: "individual" | "group",
  evaluatedAtEpochMs?: number,
) {
  const deterministicEpoch = attemptValidationEpoch(value);
  if (deterministicEpoch === null) return null;
  const validationEpoch =
    evaluatedAtEpochMs === undefined
      ? deterministicEpoch
      : evaluatedAtEpochMs;
  if (
    !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(validationEpoch) ||
    validationEpoch < deterministicEpoch
  ) {
    return null;
  }
  try {
    const validation =
      expectedMode === "individual"
        ? validateAppointmentWaitlistSubmissionAttempt(value, {
            evaluatedAtEpochMs: validationEpoch,
          })
        : validateRetiredGroupAppointmentWaitlistSubmissionAttemptForStorageMigration(
            value,
            { evaluatedAtEpochMs: validationEpoch },
          );
    return validation.valid ? validation.attempt : null;
  } catch {
    return null;
  }
}

export function cloneAppointmentWaitlistTerminalAttempt(
  value: unknown,
  evaluatedAtEpochMs?: number,
) {
  return cloneAppointmentWaitlistTerminalAttemptForMode(
    value,
    "individual",
    evaluatedAtEpochMs,
  );
}

export function cloneRetiredGroupAppointmentWaitlistTerminalAttemptForStorageMigration(
  value: unknown,
  evaluatedAtEpochMs?: number,
) {
  return cloneAppointmentWaitlistTerminalAttemptForMode(
    value,
    "group",
    evaluatedAtEpochMs,
  );
}

function cloneCommittedGatewayLineage(value: unknown) {
  try {
    const validation =
      validateSpecialistLocationGatewayNavigationAuthority(value);
    return validation.valid &&
      validation.draft.kind === "specialist-location-gateway-navigation-lineage"
      ? validation.draft
      : null;
  } catch {
    return null;
  }
}

export function appointmentWaitlistGatewayLineagesAreExact(
  left: unknown,
  right: unknown,
) {
  const leftLineage = cloneCommittedGatewayLineage(left);
  const rightLineage = cloneCommittedGatewayLineage(right);
  return Boolean(
    leftLineage &&
      rightLineage &&
      sameSnapshot(leftLineage, rightLineage),
  );
}

function appointmentWaitlistGatewayLineageMatchesAttemptForMode(input: {
  attempt: AppointmentWaitlistSubmissionAttemptV1;
  lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
  expectedMode: "individual" | "group";
}) {
  const source = input.attempt.sourceAppointmentDraft;
  const working = input.attempt.waitlistDraft.appointmentDraft;
  const sourceFingerprint = createAppointmentStepOneDraftFingerprint(source);
  const destinationFingerprint = sourceFingerprint
    ? createSpecialistLocationGatewayAppointmentDestinationFingerprintFromStepOneFingerprint(
        sourceFingerprint,
      )
    : null;
  return Boolean(
    destinationFingerprint &&
      source.entryPoint.kind === "specialist-services" &&
      working.entryPoint.kind === "specialist-services" &&
      input.lineage.customerAccountId === input.attempt.customerAccountId &&
      // The retired shared V3 flow predated the Group destination contract;
      // even a structurally valid Group request was persisted through the
      // ordinary Appointment SLG destination labelled `individual`.
      input.lineage.destinationKind === "individual" &&
      input.lineage.destinationDraftId === source.draftId &&
      input.lineage.destinationDraftFingerprint === destinationFingerprint &&
      input.lineage.selection.selectedLocationId === working.locationId &&
      input.lineage.selection.selectedUserLocationId ===
        working.entryPoint.userLocationId,
  );
}

export function appointmentWaitlistGatewayLineageMatchesAttempt(input: {
  attempt: AppointmentWaitlistSubmissionAttemptV1;
  lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
}) {
  return appointmentWaitlistGatewayLineageMatchesAttemptForMode({
    ...input,
    expectedMode: "individual",
  });
}

function appointmentWaitlistTerminalAttemptOriginMatchesForMode(input: {
  attempt: AppointmentWaitlistSubmissionAttemptV1;
  originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  gatewayLineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
  expectedMode: "individual" | "group";
}) {
  const sourceIsGateway =
    input.attempt.sourceAppointmentDraft.entryPoint.kind ===
    "specialist-services";
  const workingIsGateway =
    input.attempt.waitlistDraft.appointmentDraft.entryPoint.kind ===
    "specialist-services";
  if (input.originRequirement === "legacy-non-gateway") {
    // The pre-SLG Specialist flow uses the same public Appointment entry-point
    // shape.  The explicit aggregate discriminator, not that presentation
    // shape, separates it from an SLG transaction.  A legacy row can never be
    // upgraded because it must freeze a null lineage in the same key.
    return input.gatewayLineage === null;
  }
  return Boolean(
    sourceIsGateway &&
      workingIsGateway &&
      input.gatewayLineage &&
      appointmentWaitlistGatewayLineageMatchesAttemptForMode({
        attempt: input.attempt,
        lineage: input.gatewayLineage,
        expectedMode: input.expectedMode,
      }),
  );
}

export function appointmentWaitlistTerminalAttemptOriginMatches(input: {
  attempt: AppointmentWaitlistSubmissionAttemptV1;
  originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  gatewayLineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
}) {
  return appointmentWaitlistTerminalAttemptOriginMatchesForMode({
    ...input,
    expectedMode: "individual",
  });
}

function immutableAttemptSnapshot(
  attempt: AppointmentWaitlistSubmissionAttemptV1,
) {
  return [
    attempt.schemaVersion,
    attempt.kind,
    attempt.draftId,
    attempt.customerAccountId,
    attempt.activatedAtEpochMs,
    attempt.sourceAppointmentDraft,
    attempt.presentationSnapshot,
    attempt.waitlistDraft,
    attempt.idempotencyKey,
  ] as const;
}

export function appointmentWaitlistTerminalAttemptsHaveSameImmutableRequest(
  left: AppointmentWaitlistSubmissionAttemptV1,
  right: AppointmentWaitlistSubmissionAttemptV1,
) {
  return sameSnapshot(immutableAttemptSnapshot(left), immutableAttemptSnapshot(right));
}

export function isLegalAppointmentWaitlistTerminalAttemptStateTransition(input: {
  expected: AppointmentWaitlistSubmissionAttemptV1;
  next: AppointmentWaitlistSubmissionAttemptV1;
}) {
  if (
    !appointmentWaitlistTerminalAttemptsHaveSameImmutableRequest(
      input.expected,
      input.next,
    )
  ) {
    return false;
  }
  const expected = input.expected.state;
  const next = input.next.state;
  if (sameSnapshot(expected, next)) return true;
  if (expected.kind === "pending-reconciliation") {
    return next.kind === "accepted" || next.kind === "not-applied";
  }
  return (
    expected.kind === "not-applied" &&
    expected.reason === "mutation-not-invoked" &&
    expected.retryable &&
    next.kind === "pending-reconciliation"
  );
}

function appointmentWaitlistReceiptMatchesAcceptedAttemptForMode(input: {
  attempt: AppointmentWaitlistSubmissionAttemptV1;
  receipt: AppointmentWaitlistReceiptV3;
  expectedMode: "individual" | "group";
}) {
  if (input.attempt.state.kind !== "accepted") return false;
  const receiptInput = {
    sourceAppointmentDraft: input.attempt.sourceAppointmentDraft,
    presentationSnapshot: input.attempt.presentationSnapshot,
    waitlistDraft: input.attempt.waitlistDraft,
    idempotencyKey: input.attempt.idempotencyKey,
    submission: input.attempt.state.submission,
  };
  const expected =
    input.expectedMode === "individual"
      ? createAppointmentWaitlistReceipt(receiptInput)
      : createRetiredGroupAppointmentWaitlistReceiptForStorageMigration(
          receiptInput,
        );
  return Boolean(expected && sameSnapshot(expected, input.receipt));
}

export function appointmentWaitlistReceiptMatchesAcceptedAttempt(input: {
  attempt: AppointmentWaitlistSubmissionAttemptV1;
  receipt: AppointmentWaitlistReceiptV3;
}) {
  return appointmentWaitlistReceiptMatchesAcceptedAttemptForMode({
    ...input,
    expectedMode: "individual",
  });
}

export function createAppointmentWaitlistTerminalAttemptAggregate(input: {
  attempt: unknown;
  originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  gatewayLineage: unknown;
  resultsEntryProof?: unknown;
  evaluatedAtEpochMs: number;
}) {
  const attempt = cloneAppointmentWaitlistTerminalAttempt(
    input.attempt,
    input.evaluatedAtEpochMs,
  );
  const gatewayLineage =
    input.gatewayLineage === null
      ? null
      : cloneCommittedGatewayLineage(input.gatewayLineage);
  const resultsEntryProof = attempt
    ? cloneAppointmentWaitlistTerminalResultsEntryProofForAttempt({
        value: input.resultsEntryProof ?? null,
        sourceAppointmentDraft: attempt.sourceAppointmentDraft,
        customerAccountId: attempt.customerAccountId,
        activatedAtEpochMs: attempt.activatedAtEpochMs,
      })
    : undefined;
  if (
    !attempt ||
    attempt.state.kind !== "pending-reconciliation" ||
    resultsEntryProof === undefined ||
    (resultsEntryProof !== null &&
      resultsEntryProof.capturedAtEpochMs > input.evaluatedAtEpochMs) ||
    (!gatewayLineage && input.gatewayLineage !== null) ||
    !appointmentWaitlistTerminalAttemptOriginMatches({
      attempt,
      originRequirement: input.originRequirement,
      gatewayLineage,
    })
  ) {
    return null;
  }
  return {
    schemaVersion:
      APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_SCHEMA_VERSION,
    kind: "appointment-waitlist-terminal-attempt-aggregate",
    draftId: attempt.draftId,
    customerAccountId: attempt.customerAccountId,
    originRequirement: input.originRequirement,
    revision: 1,
    attempt,
    resultsEntryProof,
    gatewayLineage,
    receipt: null,
    acknowledgedAtEpochMs: null,
  } satisfies AppointmentWaitlistTerminalAttemptAggregateV1;
}

function validateAppointmentWaitlistTerminalAttemptAggregateForMode(
  value: unknown,
  expectedMode: "individual" | "group",
): AppointmentWaitlistTerminalAttemptAggregateValidation {
  const invalid = () => ({
    valid: false as const,
    reason:
      "invalid-appointment-waitlist-terminal-attempt-aggregate" as const,
  });
  try {
    if (
      !isRecord(value) ||
      !hasExactKeys(value, [
        "schemaVersion",
        "kind",
        "draftId",
        "customerAccountId",
        "originRequirement",
        "revision",
        "attempt",
        "resultsEntryProof",
        "gatewayLineage",
        "receipt",
        "acknowledgedAtEpochMs",
      ]) ||
      value.schemaVersion !==
        APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_SCHEMA_VERSION ||
      value.kind !== "appointment-waitlist-terminal-attempt-aggregate" ||
      !isIdentifier(value.draftId) ||
      !isIdentifier(value.customerAccountId) ||
      (value.originRequirement !== "legacy-non-gateway" &&
        value.originRequirement !== "gateway") ||
      !Number.isSafeInteger(value.revision) ||
      (value.revision as number) < 1 ||
      (value.acknowledgedAtEpochMs !== null &&
        !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(
          value.acknowledgedAtEpochMs,
        ))
    ) {
      return invalid();
    }
    const attempt = cloneAppointmentWaitlistTerminalAttemptForMode(
      value.attempt,
      expectedMode,
    );
    if (
      !attempt ||
      attempt.draftId !== value.draftId ||
      attempt.customerAccountId !== value.customerAccountId
    ) {
      return invalid();
    }
    const gatewayLineage =
      value.gatewayLineage === null
        ? null
        : cloneCommittedGatewayLineage(value.gatewayLineage);
    const resultsEntryProof =
      cloneAppointmentWaitlistTerminalResultsEntryProofForAttempt({
        value: value.resultsEntryProof,
        sourceAppointmentDraft: attempt.sourceAppointmentDraft,
        customerAccountId: attempt.customerAccountId,
        activatedAtEpochMs: attempt.activatedAtEpochMs,
      });
    if (
      resultsEntryProof === undefined ||
      (!gatewayLineage && value.gatewayLineage !== null) ||
      !appointmentWaitlistTerminalAttemptOriginMatchesForMode({
        attempt,
        originRequirement: value.originRequirement,
        gatewayLineage,
        expectedMode,
      })
    ) {
      return invalid();
    }
    let receipt: AppointmentWaitlistReceiptV3 | null = null;
    if (value.receipt !== null) {
      const validation =
        expectedMode === "individual"
          ? validateAppointmentWaitlistReceipt(value.receipt)
          : validateRetiredGroupAppointmentWaitlistReceiptForStorageMigration(
              value.receipt,
            );
      if (
        !validation.valid ||
        !appointmentWaitlistReceiptMatchesAcceptedAttemptForMode({
          attempt,
          receipt: validation.receipt,
          expectedMode,
        })
      ) {
        return invalid();
      }
      receipt = validation.receipt;
    }
    if (
      (receipt === null && value.acknowledgedAtEpochMs !== null) ||
      (receipt !== null &&
        value.acknowledgedAtEpochMs !== null &&
        value.acknowledgedAtEpochMs < receipt.submission.acceptedAtEpochMs)
    ) {
      return invalid();
    }
    return {
      valid: true,
      aggregate: {
        schemaVersion:
          APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_SCHEMA_VERSION,
        kind: "appointment-waitlist-terminal-attempt-aggregate",
        draftId: attempt.draftId,
        customerAccountId: attempt.customerAccountId,
        originRequirement: value.originRequirement,
        revision: value.revision as number,
        attempt,
        resultsEntryProof,
        gatewayLineage,
        receipt,
        acknowledgedAtEpochMs: value.acknowledgedAtEpochMs as number | null,
      },
    };
  } catch {
    return invalid();
  }
}


export function validateAppointmentWaitlistTerminalAttemptAggregate(
  value: unknown,
): AppointmentWaitlistTerminalAttemptAggregateValidation {
  return validateAppointmentWaitlistTerminalAttemptAggregateForMode(
    value,
    "individual",
  );
}

function validateAppointmentWaitlistTerminalAttemptAggregateV1ForStorageMigrationMode(
  value: unknown,
  expectedMode: "individual" | "group",
): AppointmentWaitlistTerminalAttemptAggregateV1StorageMigrationValidation {
  const invalid = () => ({
    valid: false as const,
    reason:
      "invalid-appointment-waitlist-terminal-attempt-aggregate" as const,
  });
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "schemaVersion",
      "kind",
      "draftId",
      "customerAccountId",
      "originRequirement",
      "revision",
      "attempt",
      "gatewayLineage",
      "receipt",
      "acknowledgedAtEpochMs",
    ]) ||
    value.schemaVersion !== 1
  ) {
    return invalid();
  }
  const upgradedValidation =
    validateAppointmentWaitlistTerminalAttemptAggregateForMode(
      {
        ...value,
        schemaVersion:
          APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_SCHEMA_VERSION,
        resultsEntryProof: null,
      },
      expectedMode,
    );
  if (
    !upgradedValidation.valid ||
    upgradedValidation.aggregate.resultsEntryProof !== null
  ) {
    return invalid();
  }
  const aggregate = upgradedValidation.aggregate;
  return {
    valid: true,
    aggregate: {
      schemaVersion: 1,
      kind: aggregate.kind,
      draftId: aggregate.draftId,
      customerAccountId: aggregate.customerAccountId,
      originRequirement: aggregate.originRequirement,
      revision: aggregate.revision,
      attempt: aggregate.attempt,
      gatewayLineage: aggregate.gatewayLineage,
      receipt: aggregate.receipt,
      acknowledgedAtEpochMs: aggregate.acknowledgedAtEpochMs,
    },
  };
}

export function validateIndividualAppointmentWaitlistTerminalAttemptAggregateV1ForStorageMigration(
  value: unknown,
) {
  return validateAppointmentWaitlistTerminalAttemptAggregateV1ForStorageMigrationMode(
    value,
    "individual",
  );
}

export function validateRetiredGroupAppointmentWaitlistTerminalAttemptAggregateForStorageMigration(
  value: unknown,
): AppointmentWaitlistTerminalAttemptAggregateV1StorageMigrationValidation {
  return validateAppointmentWaitlistTerminalAttemptAggregateV1ForStorageMigrationMode(
    value,
    "group",
  );
}

export function appointmentWaitlistTerminalAttemptAggregatesAreExact(
  left: unknown,
  right: unknown,
) {
  return sameSnapshot(left, right);
}
